import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

const region = import.meta.env.VITE_COGNITO_REGION ?? "us-east-1";
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "";
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID ?? "";

const pool: CognitoUserPool | null =
  userPoolId && clientId
    ? new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId })
    : null;

export interface AuthState {
  email: string;
  groups: string[];
  idToken: string;
  expiresAt: number;
}

let cached: AuthState | null = null;
const listeners = new Set<(s: AuthState | null) => void>();

function emit() {
  for (const l of listeners) l(cached);
}

export function onAuthChange(cb: (s: AuthState | null) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getAuth(): AuthState | null {
  return cached;
}

export function isAdmin(): boolean {
  return cached?.groups.includes("admin") ?? false;
}

function unpackSession(session: CognitoUserSession): AuthState {
  const idToken = session.getIdToken();
  const payload = idToken.payload as Record<string, unknown>;
  const groups = (payload["cognito:groups"] as string[] | undefined) ?? [];
  return {
    email: (payload.email as string) ?? (payload["cognito:username"] as string),
    groups,
    idToken: idToken.getJwtToken(),
    expiresAt: idToken.getExpiration() * 1000,
  };
}

export async function restoreSession(): Promise<AuthState | null> {
  if (!pool) return null;
  const user = pool.getCurrentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        cached = null;
        emit();
        return resolve(null);
      }
      cached = unpackSession(session);
      emit();
      resolve(cached);
    });
  });
}

export interface NewPasswordChallenge {
  kind: "newPasswordRequired";
  /** Continue the flow by calling this with the chosen new password. */
  complete: (newPassword: string) => Promise<AuthState>;
}

export type SignInResult = AuthState | NewPasswordChallenge;

export async function signIn(
  email: string,
  password: string,
): Promise<SignInResult> {
  if (!pool) throw new Error("Cognito is not configured");
  const user = new CognitoUser({ Username: email, Pool: pool });
  const details = new AuthenticationDetails({
    Username: email,
    Password: password,
  });
  return new Promise<SignInResult>((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => {
        cached = unpackSession(session);
        emit();
        resolve(cached);
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        // Strip Cognito-managed attributes — Cognito rejects them on the challenge response.
        const writeable = { ...userAttributes } as Record<string, unknown>;
        delete writeable.email_verified;
        delete writeable.phone_number_verified;
        delete writeable["email"];
        delete writeable.sub;
        resolve({
          kind: "newPasswordRequired",
          complete: (newPassword) =>
            new Promise<AuthState>((resolve2, reject2) => {
              user.completeNewPasswordChallenge(newPassword, writeable, {
                onSuccess: (session) => {
                  cached = unpackSession(session);
                  emit();
                  resolve2(cached);
                },
                onFailure: (err) => reject2(err),
              });
            }),
        });
      },
    });
  });
}

/**
 * Magic-link sign-in (Cognito CUSTOM_AUTH).
 *
 * Cognito hands back an auth `Session` when it issues the custom challenge, and
 * that session is what the emailed secret is redeemed against. It lives only in
 * this browser, so the link must be opened in the same browser that requested
 * it — clicking it on a phone after requesting it on a laptop cannot work. The
 * session is also short-lived (AuthSessionValidity, 15 min on this pool), which
 * is what bounds how long the link stays good.
 */
const MAGIC_SESSION_KEY = "sw.magicLink.pending";

interface PendingMagicLink {
  email: string;
  session: string;
}

/** Reads the private `Session` the SDK stashes on the user during CUSTOM_AUTH. */
function sessionOf(user: CognitoUser): string {
  return (user as unknown as { Session: string }).Session;
}

function setSessionOn(user: CognitoUser, session: string): void {
  (user as unknown as { Session: string }).Session = session;
}

/** Parse `#token=...&email=...` out of a magic-link URL fragment. */
export function parseMagicLinkFragment(hash: string): {
  token: string;
  email: string;
} | null {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const token = params.get("token");
  if (!token) return null;
  return { token, email: params.get("email") ?? "" };
}

export function hasPendingMagicLink(): boolean {
  return localStorage.getItem(MAGIC_SESSION_KEY) !== null;
}

/** Ask Cognito to email a sign-in link. Resolves once the challenge is issued. */
export async function requestMagicLink(email: string): Promise<void> {
  if (!pool) throw new Error("Cognito is not configured");
  const user = new CognitoUser({ Username: email, Pool: pool });
  user.setAuthenticationFlowType("CUSTOM_AUTH");
  return new Promise<void>((resolve, reject) => {
    user.initiateAuth(new AuthenticationDetails({ Username: email }), {
      // A challenge is always issued — even for unknown addresses, so the
      // response can't be used to enumerate users.
      customChallenge: () => {
        const pending: PendingMagicLink = { email, session: sessionOf(user) };
        localStorage.setItem(MAGIC_SESSION_KEY, JSON.stringify(pending));
        resolve();
      },
      // Cognito shouldn't hand over tokens without an answer; treat it as done.
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Redeem the secret from a magic link and complete sign-in. */
export async function completeMagicLink(token: string): Promise<AuthState> {
  if (!pool) throw new Error("Cognito is not configured");
  const raw = localStorage.getItem(MAGIC_SESSION_KEY);
  if (!raw) {
    throw new Error(
      "This link has to be opened in the same browser you requested it from. " +
        "Request a new one here to continue.",
    );
  }
  const pending = JSON.parse(raw) as PendingMagicLink;
  const user = new CognitoUser({ Username: pending.email, Pool: pool });
  user.setAuthenticationFlowType("CUSTOM_AUTH");
  setSessionOn(user, pending.session);

  return new Promise<AuthState>((resolve, reject) => {
    // The session is single-use whatever happens next, so clear it on every
    // outcome rather than leaving a dead token in storage.
    const done = () => localStorage.removeItem(MAGIC_SESSION_KEY);
    user.sendCustomChallengeAnswer(token, {
      onSuccess: (session) => {
        done();
        cached = unpackSession(session);
        emit();
        resolve(cached);
      },
      onFailure: (err) => {
        done();
        reject(err);
      },
      customChallenge: () => {
        done();
        reject(new Error("That sign-in link has expired. Request a new one."));
      },
    });
  });
}

export async function requestPasswordReset(email: string): Promise<void> {
  if (!pool) throw new Error("Cognito is not configured");
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise<void>((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      inputVerificationCode: () => resolve(),
    });
  });
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  if (!pool) throw new Error("Cognito is not configured");
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise<void>((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function signOut(): void {
  pool?.getCurrentUser()?.signOut();
  cached = null;
  emit();
}

export function authHeader(): Record<string, string> {
  return cached?.idToken ? { Authorization: `Bearer ${cached.idToken}` } : {};
}

export const cognitoConfigured = Boolean(pool);
export const cognitoRegion = region;
